import { useEffect, useState } from "react"
import { motion } from 'framer-motion';
import { Container, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField } from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab";
import { DateTime } from 'luxon';
import { toast } from "react-toastify";
import { ExportButtonContainer, ExportButton, DotLoading } from './styles';
import CssStyles from '../css.styles';
import { Header } from "../../../styles/shared/styles"
import api from '../../../service/api';
import Filter from "../../../components/Filter";
import useApp from '../../../hooks/useApp';
import { maskCpf, maskDate, maskDateTime } from '../../../utils/masks';
import { excelExport, PdfOrErrorMessage } from '../../../service/excel.service';
import { Loading } from "../../../components/Loading";
import { formatNumber } from "../../../utils/financialHelper";

const LeadSoldOtherCompetitorReport = () => {
  const { channels, insurances, lead_lost_sale: leadLostSale, competitor_brokers: competitorBrokers } = useApp();
  const [report, setReport] = useState(null)
  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [getCompetitorBroker, setCompetitorBroker] = useState(null);

  const dateNow = DateTime.now().toISODate()
  const dateLastMonth = DateTime.now().plus({ months: -1 }).toISODate()
  const defaultFilter = {
    periodOf: dateLastMonth,
    periodUntil: dateNow,
    channel: null,
    leadLostSale: null,
    insurance: null,
    sold: null,
    page: 0,
    perPage: 25,
    competitorBrokers: null,
  }
  const [filters, setFilters] = useState(defaultFilter)
  const [autoReload, setAutoReload] = useState(false)
  const soldOptions = [
    { name: "Não Vendido", value: false },
    { name: "Vendido", value: true }
  ]

  function getPlaceholderText(value) {
    return (
      (value == null || value.length == 0) ? "Todos" : ''
    )
  }

  const getFullUrl = (urlSuffix = '') => {
    const params = {
      date_start: filters.periodOf,
      date_end: filters.periodUntil,
      channel_id: filters.channel?.id,
      reason_id: filters.leadLostSale?.id,
      insurance_id: filters.insurance?.id,
      sold: filters.sold?.value,
      page: filters.page + 1, // First page in Laravel is 1 and in React is 0
      per_page: filters.perPage,
      competitor_broker_id: filters.competitorBrokers?.id,
    }

    // remove null and undefined
    const realParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined) acc[key] = value;
      return acc;
    }, {})

    const queryParams = new URLSearchParams(realParams).toString()
    const baseURL = `/leads/sold-other-competitor-report${urlSuffix}`
    return `${baseURL}?${queryParams}`
  }

  const getSoldOtherCompetitorReport = async () => {
    setIsLoading(true)
    try {
      const responseReport = await api.get(getFullUrl())
      setReport(responseReport.data.report)
      setSummary(responseReport.data.summary)

    } catch (error) {
      if (error.response && error.response.status === 422) {
        toast.warn(error.response.data.message || 'Erro de validação.');
      } else {
        toast.error('Ocorreu um erro ao buscar o relatório. Tente novamente mais tarde.');
      }

    } finally {
      setIsLoading(false)
    }
  }

  const handleExport = async (urlSuffix) => {
    setIsLoadingExport(true)

    try {
      if (urlSuffix === '-export-xls') {
        await excelExport(
          getFullUrl(urlSuffix),
          `relatorio_de_conferencia-${DateTime.now().toFormat('dd_LL_yyyy')}`
        )
      }

      if (urlSuffix === '-export-pdf') {
        await PdfOrErrorMessage(
          getFullUrl(urlSuffix),
          `relatorio_de_conferencia-${DateTime.now().toFormat('dd_LL_yyyy')}`,
          () => { } // required callBack
        )
      }

    } catch (error) {
      toast.error('Erro ao exportar o relatório', error)

    } finally {
      setIsLoadingExport(false)
    }
  }

  const updateFilters = (newValue) => {
    setFilters(prev => ({
      ...prev,
      ...newValue,
      page: 0,
    }));
  };
  const handleChange = (e) => {
    updateFilters({ [e.target.name]: e.target.value });
  };
  const handleChangeRowsPerPage = (event) => {
    setFilters({ ...filters, page: 0, perPage: parseInt(event.target.value, 10) });
    setAutoReload(true)
  };
  const handleChangePage = (event, page) => {
    setFilters({ ...filters, page });
    setAutoReload(true)
  };
  const clearFilter = () => (
    setFilters(defaultFilter)
  )

  useEffect(() => {
    setCompetitorBroker(competitorBrokers?.map((st) => ({ id: st.var_id, name: st.value })) || '');
  }, [filters]);

  useEffect(() => {
    if (autoReload)
      getSoldOtherCompetitorReport()
    setAutoReload(false)
  }, [autoReload])

  return (
    <>
      <motion.div
        initial={CssStyles.motionDivInitial}
        animate={CssStyles.motionDivAnimate}
        exit={CssStyles.motionDivExit}>

        <Container style={{ marginTop: '10px' }}>
          <Header>
            <h1>Relatório de Conferência</h1>
          </Header>
          <h3>Leads vendidos, não vendidos ou vendidos para seguradoras diferentes das originais.</h3>

          <Filter clickFilter={getSoldOtherCompetitorReport} clearFilter={clearFilter} buttonStyle={{ "height": "fit-content" }}>
            <Grid
              container
              style={{ columnGap: '15px', rowGap: '10px' }}
            >
              <label>
                Vigência de:
                <input
                  type="date"
                  name="periodOf"
                  className="input-form"
                  title='De'
                  value={filters.periodOf}
                  onChange={handleChange}
                />
              </label>


              <label>
                Até
                <input
                  type="date"
                  name="periodUntil"
                  className="input-form"
                  title='Até'
                  value={filters.periodUntil}
                  onChange={handleChange}
                />
              </label>

              <Autocomplete
                xs={4}
                options={channels || []}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.channel || null}
                onChange={(e, value) => updateFilters({ channel: value })}
                renderInput={(params) => (
                  <TextField {...params}
                    className="autocomplete"
                    variant="filled"
                    placeholder={getPlaceholderText(filters.channel)}
                    label='Canal Origem'
                  />
                )}
                style={{
                  width: '300px'
                }}
              />

              <Autocomplete
                xs={4}
                options={leadLostSale || []}
                getOptionLabel={(option) => option?.value || ''}
                value={filters.leadLostSale || null}
                onChange={(e, value) => updateFilters({ leadLostSale: value })}
                renderInput={(params) => (
                  <TextField {...params}
                    className="autocomplete"
                    variant="filled"
                    placeholder={getPlaceholderText(filters.leadLostSale)}
                    label='Motivo não venda'
                  />
                )}
                style={{
                  width: '300px'
                }}
              />

              <Autocomplete
                xs={4}
                options={insurances || []}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.insurance || null}
                onChange={(e, value) => updateFilters({ insurance: value })}
                renderInput={(params) => (
                  <TextField {...params}
                    className="autocomplete"
                    variant="filled"
                    placeholder={getPlaceholderText(filters.insurance)}
                    label='Tipo de Seguro'
                  />
                )}
                style={{
                  width: '300px'
                }}
              />

              <Autocomplete
                xs={4}
                options={soldOptions || []}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.sold || null}
                onChange={(e, value) => updateFilters({ sold: value })}
                renderInput={(params) => (
                  <TextField {...params}
                    className="autocomplete"
                    variant="filled"
                    placeholder={getPlaceholderText(filters.sold)}
                    label='Status da venda'
                  />
                )}
                style={{
                  width: '300px'
                }}
              />

              <Autocomplete
                xs={4}
                options={getCompetitorBroker || []}
                getOptionLabel={(option) => option?.name || ''}
                value={filters.competitorBrokers || [0]}
                onChange={(e, value) => setFilters({...filters, competitorBrokers: value})}
                renderInput={(params) => (
                  <TextField {...params} 
                    className="autocomplete" 
                    variant="filled" 
                    placeholder={getPlaceholderText(filters.competitorBrokers)}
                    label='Vendido na Seguradora'
                  />
                )}
                style={{
                  width: '300px'
                }}
              />              
            </Grid>
          </Filter>

          <TablePagination
            component="div"
            count={report?.total ?? 0}
            page={filters.page}
            onPageChange={handleChangePage}
            rowsPerPage={filters.perPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Leads por página:"
            nextIconButtonText="Próxima página"
            backIconButtonText="Página anterior"
          />

          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Loading key='loading' />
            </div>
          )}

          {report && report.data && summary && (
            <>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Table style={{ width: 'fit-content' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontWeight: 'bold' }}>Conversão</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontWeight: 'bold' }}>Número de Leads</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontWeight: 'bold' }}>Não Vendidos</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontWeight: 'bold' }}>Vendidos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontSize: '2rem', backgroundColor: '#fff', border: '1px solid rgb(224 224 224)', width: '192px' }}>{`${formatNumber(summary.conversion)}%`}</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontSize: '2rem', backgroundColor: '#fff', border: '1px solid rgb(224 224 224)', width: '192px' }}>{summary.leads_total}</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontSize: '2rem', backgroundColor: '#fff', border: '1px solid rgb(224 224 224)', width: '192px' }}>{summary.leads_not_sold}</TableCell>
                      <TableCell style={{ padding: 0, textAlign: 'center', fontSize: '2rem', backgroundColor: '#fff', border: '1px solid rgb(224 224 224)', width: '192px' }}>{summary.leads_sold}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <TableContainer component={Paper} style={{ marginTop: '10px', borderRadius: '8px', paddingBottom: '15px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }}>Lead</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Cliente</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>CPF</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Placa</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Seguradora Origem</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Motivo Não venda</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Tipo Seguro</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Data Venda</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Status Venda</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Vendido na Seguradora</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Lead Finalizado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      report.data.map((lead) => (
                        <TableRow>
                          <TableCell>{lead.lead_id}</TableCell>
                          <TableCell>{lead.client_name}</TableCell>
                          <TableCell>{maskCpf(lead.client_cpf)}</TableCell>
                          <TableCell>{lead.details?.license_plate || '-'}</TableCell>
                          <TableCell>{lead.channel_name}</TableCell>
                          <TableCell>{lead.reason_name}</TableCell>
                          <TableCell>{lead.insurance_name}</TableCell>
                          <TableCell>{maskDateTime(lead.sold_date)}</TableCell>
                          <TableCell>{lead.sold_status}</TableCell>
                          <TableCell>{lead.competitor_name}</TableCell>
                          <TableCell>{maskDateTime(lead.end_lead_date)}</TableCell>
                        </TableRow>
                      ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>

              <ExportButtonContainer>
                <ExportButton
                  type="button"
                  onClick={() => handleExport('-export-xls')}
                >
                  {isLoadingExport ? (
                    <>
                      Gerando
                      <DotLoading />
                    </>
                  ) : (
                    'Exportar Xls'
                  )}
                </ExportButton>

                <ExportButton
                  type="button"
                  onClick={() => handleExport('-export-pdf')}
                >
                  {isLoadingExport ? (
                    <>
                      Gerando
                      <DotLoading />
                    </>
                  ) : (
                    'Exportar Pdf'
                  )}
                </ExportButton>
              </ExportButtonContainer>
            </>
          )}

        </Container>
      </motion.div>
    </>
  )
}

export default LeadSoldOtherCompetitorReport